import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, User, Truck, Sparkles, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE_META = {
  vendor: { path: "/vendors", label: "vendor", icon: Truck, color: "#B77B68", nameKey: "name" },
  agency: { path: "/agencies", label: "agency", icon: Sparkles, color: "#A18E9F", nameKey: "name" },
  staff: { path: "/staff", label: "staff", icon: User, color: "#7A9BA0", nameKey: "name" },
  payroll: { path: "/payroll", label: "payroll", icon: DollarSign, color: "#7D9B7A", nameKey: "staff_name" },
};

/**
 * type: 'vendor' | 'agency' | 'staff' | 'payroll'
 * value: assignee_id
 * onChange: (assignee_id, assignee_name) => void
 */
export default function AssigneeCombobox({ type, value, onChange, testid = "assignee-combobox" }) {
  const meta = TYPE_META[type];
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!type) return setItems([]);
    api.get(meta.path).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => items.find((i) => i.id === value), [items, value]);
  const Icon = meta?.icon;

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter((i) => (i[meta.nameKey] || "").toLowerCase().includes(q));
  }, [items, query, meta]);

  if (!type) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          type="button"
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-[13px] text-left hover:border-black/25 transition-colors"
        >
          <span className="flex items-center gap-2 min-w-0">
            {Icon && (
              <span
                className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: `${meta.color}18`, color: meta.color }}
              >
                <Icon className="w-3 h-3" />
              </span>
            )}
            <span className={`truncate ${selected ? "text-[#1C1C1E]" : "text-[#8E8E93]"}`}>
              {selected ? selected[meta.nameKey] : `Select ${meta.label}…`}
            </span>
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-[#8E8E93] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testid}-popover`}
        align="start"
        className="p-0 rounded-2xl border-black/[0.06] shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false} className="rounded-2xl">
          <CommandInput
            placeholder={`Search ${meta.label}…`}
            value={query}
            onValueChange={setQuery}
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-56">
            <CommandEmpty>
              <div className="py-4 text-[12px] text-[#8E8E93] text-center">
                No {meta.label} matches "{query}".
              </div>
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((item) => {
                const name = item[meta.nameKey];
                const active = item.id === value;
                return (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => {
                      onChange(item.id, name);
                      setOpen(false);
                    }}
                    data-testid={`${testid}-item-${item.id}`}
                    className="cursor-pointer rounded-lg"
                  >
                    <Check className={cn("mr-2 h-4 w-4", active ? "opacity-100 text-[#A9684E]" : "opacity-0")} />
                    <span className="flex-1 truncate">{name}</span>
                    {item.category && <span className="ml-2 text-[10.5px] text-[#8E8E93]">{item.category}</span>}
                    {item.role && <span className="ml-2 text-[10.5px] text-[#8E8E93]">{item.role}</span>}
                    {item.type && !item.role && <span className="ml-2 text-[10.5px] text-[#8E8E93]">{item.type}</span>}
                    {item.period && <span className="ml-2 text-[10.5px] text-[#8E8E93] font-mono">{item.period}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
